import { z } from "zod";
import {
  CAMPAIGN_OVERRIDABLE_CONTEXT_KEYS,
  type ContextKey,
  contextValuePolicy,
} from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";

/**
 * Which facts a rep may answer once and have every later draft in the workspace use.
 *
 * Deliberately a subset of the context keys, and the list is the product decision: a key is here
 * only when one standing answer is true for every conversation in its scope. Three kinds of key are
 * excluded, and the reasons differ enough to be worth stating, because the next person's instinct
 * will be that the list is simply short.
 *
 * - **Per conversation.** `availability`, `booking_context`, `recipient_timezone`, `prospect_name`
 *   and `follow_up_promises` are facts about one thread or one person. A standing answer for them
 *   would be offered to the next lead as though it were about them, which is the failure
 *   `refusePersonalData` guards the other half of.
 * - **Campaign configuration.** `campaign_goal` and `next_step_policy` are written only by
 *   `configureCampaignConfiguration`; `ContextStore.putBatch` refuses them from any other writer,
 *   so a FAQ row holding one could be saved here and would then fail to project. Keeping them out
 *   means the refusal happens where the rep is, not in a background job.
 * - **Style, not fact.** `tone_preferences` and `approved_examples` are how the seller writes. The
 *   design keeps voice in its own channel because an entry carrying tone puts a style instruction
 *   into the evidence channel the grounding judge reads as fact.
 *
 * `calendar_mode` is absent for the campaign-configuration reason in spirit: intake derives it from
 * the campaign's own connector on every poll, so a rep answer would be overwritten silently.
 */
export interface StandingFactKey {
  readonly key: ContextKey;
  /** What the rep is asked, in their words rather than the key's. */
  readonly label: string;
  /** What a good answer looks like, shown under the field. */
  readonly hint: string;
  /** One value, or a list this entry is one of. Decides whether a second row is a contradiction. */
  readonly policy: "atomic" | "validated" | "expandable";
  /** Whether one campaign of this workspace may hold a different answer. */
  readonly campaignOverridable: boolean;
  /**
   * Which fields the rep fills, and therefore which value the host composes from them.
   *
   * The browser never builds a context value. It posts the fields a person typed and the host
   * assembles the shape the key's own schema demands, so a change to that schema is a change in one
   * place rather than a form that quietly starts posting the wrong object.
   */
  readonly answerShape: StandingAnswerShape;
}

type StandingAnswerShape =
  /** One box of prose. */
  | "text"
  /** Minutes. */
  | "number"
  /** What leads ask, and what to answer. */
  | "qa"
  /** What a reply must never say, and why. */
  | "guard"
  /** A link, what it is, and when it goes out. */
  | "resource"
  /** The eight fields of a booking policy. */
  | "scheduling";

const CATALOG: readonly Omit<StandingFactKey, "policy" | "campaignOverridable">[] = [
  {
    key: "seller_offer",
    label: "What you offer",
    hint: "One or two sentences a reply may use to say what you do. Not a pitch.",
    answerShape: "text",
  },
  {
    key: "seller_name",
    label: "Who the reply is from",
    hint: "The company name a lead would recognise.",
    answerShape: "text",
  },
  {
    key: "seller_identity",
    label: "How you describe yourselves",
    hint: "The one-line description of the company a reply may repeat.",
    answerShape: "text",
  },
  {
    key: "answers_when_asked",
    label: "Answer, when a lead asks",
    hint: "Say what the answer is, not that there is one. Used only when a lead asks about it.",
    answerShape: "qa",
  },
  {
    key: "approved_facts",
    label: "A fact a reply may state",
    hint: "Something true a reply may say without being asked.",
    answerShape: "text",
  },
  {
    key: "reply_guards",
    label: "Never say",
    hint: "What a reply must not say or imply. Never shown to a lead, and it authorises nothing.",
    answerShape: "guard",
  },
  {
    key: "seller_resources",
    label: "A link a reply may share",
    hint: "An https link, what it is in your words, and whether it goes out unprompted.",
    answerShape: "resource",
  },
  {
    key: "meeting_duration_minutes",
    label: "How long the call is",
    hint: "Minutes, as a number.",
    answerShape: "number",
  },
  {
    key: "scheduling_policy",
    label: "How you book",
    hint: "The hours you take calls, how much notice you need, and how far ahead you book.",
    answerShape: "scheduling",
  },
  {
    key: "meeting_hours",
    label: "When you take calls",
    hint: "The hours a reply may offer.",
    answerShape: "text",
  },
  {
    key: "qualification_context",
    label: "Who this is for",
    hint: "What makes a lead a fit for this campaign.",
    answerShape: "text",
  },
  {
    key: "language",
    label: "Which language replies are written in",
    hint: "The language name, as a lead would read it.",
    answerShape: "text",
  },
];

export const STANDING_FACT_CATALOG: readonly StandingFactKey[] = CATALOG.map((entry) => ({
  ...entry,
  policy: contextValuePolicy[entry.key],
  campaignOverridable: CAMPAIGN_OVERRIDABLE_CONTEXT_KEYS.has(entry.key),
}));

const BY_KEY = new Map<string, StandingFactKey>(
  STANDING_FACT_CATALOG.map((entry) => [entry.key, entry]),
);

/** A key a rep may hold a standing answer for, or undefined for every other context key. */
export function standingFactKey(key: string): StandingFactKey | undefined {
  return BY_KEY.get(key);
}

/**
 * Facts the agent stopped for that no standing answer could have prevented.
 *
 * Shown in the follow-up form rather than hidden, so a rep who watched the agent stop for one does
 * not go looking for the field that would have answered it. Each carries why, because "not on the
 * list" invites the reasonable guess that it was forgotten.
 */
export const NOT_A_STANDING_FACT: Readonly<Record<string, string>> = {
  availability: "Planned per conversation from the connected calendar, never a standing setting.",
  booking_context: "A record of one booking in one thread, not a setting.",
  recipient_timezone: "Confirmed for the person who wrote, and only for that thread.",
  prospect_name: "The lead's own name.",
  campaign_goal: "Campaign configuration, changed with the campaign rather than here.",
  next_step_policy: "Campaign configuration, changed with the campaign rather than here.",
  calendar_mode: "Read from the campaign's own connector on every poll.",
  tone_preferences: "How you write, kept apart from what is true.",
  approved_examples: "How you write, kept apart from what is true.",
  follow_up_promises: "Authorised one at a time, for one thread.",
  rep_timezone: "The rep's own zone.",
  feedback: "What a rep said about one draft.",
  commitments: "What was promised in one thread.",
};

/**
 * What each of those facts is called on the page, so the list names them rather than showing the
 * key a rep has never seen. `faq-paste.test.ts` holds this to the same keys as the reasons.
 */
export const NOT_A_STANDING_FACT_LABEL: Readonly<Record<string, string>> = {
  availability: "Times to offer",
  booking_context: "Whether this lead booked",
  recipient_timezone: "The lead's timezone",
  prospect_name: "The lead's name",
  campaign_goal: "The campaign's goal",
  next_step_policy: "The campaign's next step",
  calendar_mode: "How the campaign books",
  tone_preferences: "Tone",
  approved_examples: "Example replies",
  follow_up_promises: "A promise to follow up",
  rep_timezone: "Your timezone",
  feedback: "Feedback on one draft",
  commitments: "What was promised",
};

/**
 * The most a booking policy may hold: 30 days' notice in hours, and a year ahead in days. Named so
 * the form refuses the same numbers the route does, in words, before the rep posts.
 */
export const MIN_NOTICE_HOURS_LIMIT = 720;
export const MAX_HORIZON_DAYS_LIMIT = 365;

/**
 * The fields a rep fills, one variant per shape.
 *
 * Validated at the route, so a browser that posts the wrong variant for a key is refused there
 * rather than producing a context value the pipeline would later fail to read.
 */
export const StandingAnswerFieldsSchema = z.discriminatedUnion("shape", [
  z.strictObject({ shape: z.literal("text"), text: z.string().min(1).max(10_000) }),
  z.strictObject({ shape: z.literal("number"), minutes: z.number().int().min(1).max(480) }),
  z.strictObject({
    shape: z.literal("qa"),
    askedAbout: z.string().min(1).max(500),
    text: z.string().min(1).max(10_000),
  }),
  z.strictObject({
    shape: z.literal("guard"),
    avoid: z.string().min(1).max(10_000),
    note: z.string().min(1).max(10_000).nullable().default(null),
  }),
  z.strictObject({
    shape: z.literal("resource"),
    url: z.url({ protocol: /^https$/ }),
    label: z.string().min(1).max(500),
    share: z.enum(["first_reply", "on_request"]),
  }),
  z.strictObject({
    shape: z.literal("scheduling"),
    hostTimezone: z.string().min(1).max(64),
    workingDays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    startTime: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u),
    endTime: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u),
    bufferBeforeMinutes: z.number().int().nonnegative().max(480),
    bufferAfterMinutes: z.number().int().nonnegative().max(480),
    minNoticeHours: z.number().nonnegative().max(MIN_NOTICE_HOURS_LIMIT),
    maxHorizonDays: z.number().int().positive().max(MAX_HORIZON_DAYS_LIMIT),
  }),
]);
export type StandingAnswerFields = z.infer<typeof StandingAnswerFieldsSchema>;

type TextFields = Extract<StandingAnswerFields, { shape: "text" }>;
type NumberFields = Extract<StandingAnswerFields, { shape: "number" }>;
type QaFields = Extract<StandingAnswerFields, { shape: "qa" }>;
type GuardFields = Extract<StandingAnswerFields, { shape: "guard" }>;
type ResourceFields = Extract<StandingAnswerFields, { shape: "resource" }>;
type SchedulingFields = Extract<StandingAnswerFields, { shape: "scheduling" }>;

/** The six context values composed from validated standing-answer fields. */
export type StandingAnswerValue =
  | TextFields["text"]
  | NumberFields["minutes"]
  | { id: string; asked_about: QaFields["askedAbout"]; answer: QaFields["text"] }
  | { id: string; avoid: GuardFields["avoid"]; note?: NonNullable<GuardFields["note"]> }
  | { url: ResourceFields["url"]; label: ResourceFields["label"]; share: ResourceFields["share"] }
  | {
      host_timezone: SchedulingFields["hostTimezone"];
      working_days: SchedulingFields["workingDays"];
      start_time: SchedulingFields["startTime"];
      end_time: SchedulingFields["endTime"];
      buffer_before_minutes: SchedulingFields["bufferBeforeMinutes"];
      buffer_after_minutes: SchedulingFields["bufferAfterMinutes"];
      min_notice_hours: SchedulingFields["minNoticeHours"];
      max_horizon_days: SchedulingFields["maxHorizonDays"];
    };

/**
 * Builds the value a key's schema demands out of what a person typed.
 *
 * The entry id becomes the stored entry's own id for the expandable shapes, because that is the
 * unit a narrower scope overrides: two scopes stating the same fact collapse to one rather than
 * appearing twice, and only an id makes that possible.
 */
export function composeStandingAnswer(
  fields: StandingAnswerFields,
  entryId: string,
): StandingAnswerValue {
  switch (fields.shape) {
    case "text":
      return fields.text.trim();
    case "number":
      return fields.minutes;
    case "qa":
      return { id: entryId, asked_about: fields.askedAbout.trim(), answer: fields.text.trim() };
    case "guard":
      return {
        id: entryId,
        avoid: fields.avoid.trim(),
        ...(fields.note === null ? {} : { note: fields.note.trim() }),
      };
    case "resource":
      return { url: fields.url, label: fields.label.trim(), share: fields.share };
    case "scheduling":
      return {
        host_timezone: fields.hostTimezone,
        working_days: fields.workingDays,
        start_time: fields.startTime,
        end_time: fields.endTime,
        buffer_before_minutes: fields.bufferBeforeMinutes,
        buffer_after_minutes: fields.bufferAfterMinutes,
        min_notice_hours: fields.minNoticeHours,
        max_horizon_days: fields.maxHorizonDays,
      };
  }
}

/**
 * The rep's own phrasing of what this row answers, stored beside the value.
 *
 * Only the question-and-answer shape carries one inherently; for every other shape the rep may add
 * one, and the catalog's label is not it — a label describes the field, a question describes what a
 * lead asked.
 */
export function standingAnswerQuestion(
  fields: StandingAnswerFields,
  supplied: string | null,
): string | null {
  if (fields.shape === "qa") return fields.askedAbout.trim();
  return supplied === null || supplied.trim().length === 0 ? null : supplied.trim();
}
